import { useEffect, useState } from 'react';
import { fetchAppLinks, peekAppLinks, type AppLinks } from '../lib/appLinks';

/**
 * "Or get the AquaStar app for your phone" footer.
 *
 * Renders official-looking iOS App Store + Google Play badges. Uses
 * inline SVG for the badges so we don't depend on Apple/Google's
 * hosted PNGs (which their guidelines say to avoid hot-linking).
 *
 * AQUASTAR-369 (2026-09-09): the links are no longer build-time
 * constants. They come from `GET /v1/app/aquastar/links` — set per
 * environment by the AquaStar admin on Settings › App links (TestFlight /
 * Firebase on dev+UAT, the store listings on prod). A badge whose link
 * is unset is NOT rendered (the old constants pointed at a delisted app
 * and 404ed). Nothing renders until the links have loaded.
 */
export function StoreBadges({ className = '' }: { className?: string }) {
  const [links, setLinks] = useState<AppLinks | null>(peekAppLinks());
  useEffect(() => {
    let alive = true;
    void fetchAppLinks().then((l) => {
      if (alive) setLinks(l);
    });
    return () => {
      alive = false;
    };
  }, []);
  if (!links || (!links.iosStoreUrl && !links.androidStoreUrl)) return null;
  return (
    <div
      className={`d-flex flex-wrap gap-2 align-items-center justify-content-center ${className}`}
      data-testid="store-badges"
    >
      {links.iosStoreUrl && (
        <a
          href={links.iosStoreUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Download AquaStar on the App Store"
          style={{ display: 'inline-block' }}
        >
          <AppStoreBadge />
        </a>
      )}
      {links.androidStoreUrl && (
        <a
          href={links.androidStoreUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Get AquaStar on Google Play"
          style={{ display: 'inline-block' }}
        >
          <GooglePlayBadge />
        </a>
      )}
    </div>
  );
}

function AppStoreBadge() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="160"
      height="48"
      viewBox="0 0 160 48"
      role="img"
      aria-label="Download on the App Store"
    >
      <rect
        x="0.5"
        y="0.5"
        width="159"
        height="47"
        rx="7.5"
        fill="#000"
        stroke="#A6A6A6"
      />
      <g fill="#fff">
        <path d="M30.7 24.7c0-2.4 2-3.6 2.1-3.6-1.2-1.7-2.9-1.9-3.5-2-1.5-.2-2.9.9-3.7.9-.8 0-1.9-.9-3.2-.8-1.6 0-3.2 1-4 2.4-1.7 3-.4 7.4 1.2 9.8.8 1.2 1.8 2.5 3.1 2.5 1.2-.1 1.7-.8 3.2-.8s1.9.8 3.2.8c1.3 0 2.2-1.2 3-2.4.9-1.4 1.3-2.7 1.4-2.8-.1 0-2.7-1-2.8-4ZM28.4 17.5c.7-.8 1.1-2 1-3.2-1 0-2.2.7-2.9 1.5-.6.7-1.2 1.9-1 3.1 1.1.1 2.3-.6 2.9-1.4Z" />
        <text
          x="46"
          y="20"
          fontFamily="-apple-system, BlinkMacSystemFont, sans-serif"
          fontSize="9"
          fill="#fff"
        >
          Download on the
        </text>
        <text
          x="46"
          y="34"
          fontFamily="-apple-system, BlinkMacSystemFont, sans-serif"
          fontSize="16"
          fontWeight="600"
          fill="#fff"
        >
          App Store
        </text>
      </g>
    </svg>
  );
}

function GooglePlayBadge() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="160"
      height="48"
      viewBox="0 0 160 48"
      role="img"
      aria-label="Get it on Google Play"
    >
      <rect
        x="0.5"
        y="0.5"
        width="159"
        height="47"
        rx="7.5"
        fill="#000"
        stroke="#A6A6A6"
      />
      <g>
        <path
          d="M22.4 14.9c-.4.4-.6 1-.6 1.7v14.7c0 .8.2 1.4.6 1.8l5.7-9.1-5.7-9.1Z"
          fill="#5BC9F4"
        />
        <path
          d="m32 25 5.7-3.3-9.6-5.5c-.6-.4-1.2-.5-1.7-.3l5.6 9.1Z"
          fill="#5DBE3F"
        />
        <path
          d="M28 33.1c.5.2 1.1.1 1.7-.3l9.6-5.5L33.7 24l-5.7 9.1Z"
          fill="#F2BC42"
        />
        <path
          d="m37.7 27.3 4.5-2.6c.8-.5 1.2-1.1 1.2-1.7s-.4-1.3-1.2-1.7l-4.5-2.6L32 24l5.7 3.3Z"
          fill="#E8403E"
        />
        <text
          x="56"
          y="20"
          fontFamily="-apple-system, BlinkMacSystemFont, sans-serif"
          fontSize="9"
          fill="#fff"
        >
          GET IT ON
        </text>
        <text
          x="56"
          y="34"
          fontFamily="-apple-system, BlinkMacSystemFont, sans-serif"
          fontSize="16"
          fontWeight="600"
          fill="#fff"
        >
          Google Play
        </text>
      </g>
    </svg>
  );
}
